import { useState, useEffect, useCallback } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { signOut } from "aws-amplify/auth"
import { getCachedAuthSession } from "../utils/userAttributesCache";
import { getUrl } from "aws-amplify/storage";
import { useSubscriptionContext } from '../contexts/SubscriptionContext';
import { NotificationBell, NotificationDrawer } from "./NotificationDrawer";
import { getCurrentUser } from "aws-amplify/auth";

import logo from "../assets/images/logo_skill-blue.25.svg";
import defaultProfileImage from "../assets/images/logo_sb-blue.25.svg";
import "./NavigationLight.css";

function NavigationLight() {
  const navigate = useNavigate();
  const location = useLocation();
  const [profilePhoto, setProfilePhoto] = useState<string | null>(null);
  const [firstName, setFirstName] = useState<string | null>(null);
  const [lastName, setLastName] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notificationDrawerOpen, setNotificationDrawerOpen] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const { isPro, subscriptionDetails } = useSubscriptionContext();

  const fetchProfilePhoto = useCallback(async () => {
    try {
      const { fetchUserAttributes } = await import("aws-amplify/auth");
      const attributes = await fetchUserAttributes();

      if (attributes.picture) {
        try {
          const photoUrl = await getUrl({ key: attributes.picture });
          setProfilePhoto(photoUrl.url.toString());
        } catch {
          // Ignore photo loading errors
        }
      }

      if (attributes.given_name) {
        setFirstName(attributes.given_name);
      }
      if (attributes.family_name) {
        setLastName(attributes.family_name);
      }
    } catch {
      // Ignore auth errors
    }
  }, []);

  useEffect(() => {
    (window as { refreshNavProfilePhoto?: () => void }).refreshNavProfilePhoto =
      fetchProfilePhoto;
  }, [fetchProfilePhoto]);

  const checkAdminAccess = async () => {
    try {
      await getCurrentUser();
      const session = await getCachedAuthSession();

      const idToken = session.tokens?.idToken;
      const groups = idToken?.payload["cognito:groups"] as string[] | undefined;

      const hasAdminAccess = groups?.includes("admin") || false;
      setIsAdmin(hasAdminAccess);
    } catch {
      setIsAdmin(false);
    }
  };


  useEffect(() => {
    fetchProfilePhoto();
    checkAdminAccess();

    const handleStorageChange = () => {
      fetchProfilePhoto();
    };

    window.addEventListener("profilePhotoUpdated", handleStorageChange);
    window.addEventListener("profileUpdated", handleStorageChange);

    return () => {
      window.removeEventListener("profilePhotoUpdated", handleStorageChange);
      window.removeEventListener("profileUpdated", handleStorageChange);
    };
  }, [fetchProfilePhoto]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest(".nav-light")) {
        setMobileMenuOpen(false);
        setUserMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setUserMenuOpen(false);
  }, [location.pathname]);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate("/login");
    } catch {
      // Ignore sign out errors
    }
  };

  const navItems = [
    { path: "/app/skillpackages", label: "Skills" },
    { path: "/app/certifications", label: "Certifications" },
    { path: "/app/frameworks", label: "Frameworks", proOnly: true },
    { path: "/app/competencies", label: "Competencies" },
    { path: "/app/shared-skills", label: "Shared", proOnly: true },
  ];

  const settingsItems = [
    { path: "/settings/profile", label: "Profile" },
    { path: "/settings/preferences", label: "Preferences" },
    { path: "/settings/subscription", label: "Subscription" },
    { path: "/settings/security", label: "Security" },
    { path: "/settings/sharing", label: "Sharing" },
    { path: "/settings/notifications", label: "Notifications" },
    { path: "/settings/integrations", label: "AI & Integrations" },
  ];

  const adminItems = [
    { path: "/admin/seed-data", label: "Seed Data" },
    { path: "/admin/manage-company-subscriptions", label: "Companies" },
    { path: "/app/admin/package-moderation", label: "Package Moderation" },
  ];

  return (
    <nav className="nav-light">
      <div className="nav-light__container">
        {/* Logo */}
        <NavLink to="/app" className="nav-light__logo">
          <img src={logo} alt="SkillBubble" height="36" />
        </NavLink>

        {/* Desktop Navigation */}
        <div className="nav-light__desktop">
          <div className="nav-light__links">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.proOnly && !isPro ? "#" : item.path}
                className={({ isActive }) =>
                  `nav-light__link ${isActive ? "nav-light__link--active" : ""} ${
                    item.proOnly && !isPro ? "nav-light__link--locked" : ""
                  }`
                }
                onClick={(e) => {
                  if (item.proOnly && !isPro) {
                    e.preventDefault();
                    navigate("/settings/subscription");
                  }
                }}
              >
                <span className="nav-light__link-text">{item.label}</span>
                {item.proOnly && !isPro && (
                  <span className="nav-light__lock">🔒</span>
                )}
              </NavLink>
            ))}
          </div>

          <div className="nav-light__actions">
            {/* Unified notification bell */}
            <NotificationBell
              totalCount={notificationCount}
              onClick={() => setNotificationDrawerOpen(true)}
              isOpen={notificationDrawerOpen}
            />

            {/* User Menu */}
            <div className="nav-light__user">
              <button
                className="nav-light__user-button"
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                aria-expanded={userMenuOpen}
                aria-label="User menu"
              >
                <img
                  src={profilePhoto || defaultProfileImage}
                  alt="Profile"
                  className="nav-light__avatar"
                />
                <div className="nav-light__user-info">
                  <span className="nav-light__user-name">
                    {firstName
                      ? `${firstName}${lastName ? ` ${lastName.charAt(0)}.` : ""}`
                      : "Account"}
                  </span>
                  <span
                    className={`nav-light__badge nav-light__badge--${isPro ? "pro" : "free"}`}
                  >
                    {isPro
                      ? `Pro${subscriptionDetails?.status === "trialing" ? " Trial" : ""}`
                      : "Free"}
                  </span>
                </div>
                <svg
                  className={`nav-light__chevron ${userMenuOpen ? "nav-light__chevron--open" : ""}`}
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <path
                    d="M4 6L8 10L12 6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              {/* User Dropdown */}
              {userMenuOpen && (
                <div className="nav-light__dropdown">
                  {/* Settings items */}
                  {settingsItems.map((item) => (
                    <button
                      key={item.path}
                      className="nav-light__dropdown-item"
                      onClick={() => {
                        navigate(item.path);
                        setUserMenuOpen(false);
                      }}
                    >
                      <span>{item.label}</span>
                    </button>
                  ))}

                  {/* Admin items */}
                  {isAdmin && (
                    <>
                      <div className="nav-light__dropdown-divider" />
                      {adminItems.map((item) => (
                        <button
                          key={item.path}
                          className="nav-light__dropdown-item"
                          onClick={() => {
                            navigate(item.path);
                            setUserMenuOpen(false);
                          }}
                        >
                          <span>{item.label}</span>
                        </button>
                      ))}
                    </>
                  )}

                  {/* Sign out */}
                  <div className="nav-light__dropdown-divider" />
                  <button
                    className="nav-light__dropdown-item nav-light__dropdown-item--danger"
                    onClick={handleSignOut}
                  >
                    <span>Sign Out</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Menu Button */}
        <div className="nav-light__mobile-actions">
          <NotificationBell
            totalCount={notificationCount}
            onClick={() => setNotificationDrawerOpen(true)}
            isOpen={notificationDrawerOpen}
          />
          <button
            className="nav-light__mobile-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-expanded={mobileMenuOpen}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
          >
            {mobileMenuOpen ? (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M18 6L6 18M6 6L18 18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            ) : (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M3 12H21M3 6H21M3 18H21"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="nav-light__mobile-menu">
          {/* User Info */}
          <div className="nav-light__mobile-user">
            <img
              src={profilePhoto || defaultProfileImage}
              alt="Profile"
              className="nav-light__mobile-avatar"
            />
            <div className="nav-light__mobile-user-info">
              <span className="nav-light__mobile-user-name">
                {firstName
                  ? `${firstName}${lastName ? ` ${lastName.charAt(0)}.` : ""}`
                  : "Account"}
              </span>
              <span
                className={`nav-light__badge nav-light__badge--${isPro ? "pro" : "free"}`}
              >
                {isPro
                  ? `Pro${subscriptionDetails?.status === "trialing" ? " Trial" : ""}`
                  : "Free"}
              </span>
            </div>
          </div>

          {/* Navigation Links */}
          <div className="nav-light__mobile-section">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.proOnly && !isPro ? "#" : item.path}
                className={({ isActive }) =>
                  `nav-light__mobile-link ${isActive ? "nav-light__mobile-link--active" : ""} ${
                    item.proOnly && !isPro
                      ? "nav-light__mobile-link--locked"
                      : ""
                  }`
                }
                onClick={(e) => {
                  if (item.proOnly && !isPro) {
                    e.preventDefault();
                    navigate("/settings/subscription");
                  }
                }}
              >
                <span>{item.label}</span>
                {item.proOnly && !isPro && (
                  <span className="nav-light__lock">🔒</span>
                )}
              </NavLink>
            ))}
          </div>

          {/* Settings */}
          <div className="nav-light__mobile-section">
            <div className="nav-light__mobile-header">Settings</div>
            {settingsItems.map((item) => (
              <button
                key={item.path}
                className="nav-light__mobile-link"
                onClick={() => navigate(item.path)}
              >
                <span>{item.label}</span>
              </button>
            ))}
          </div>

          {/* Admin */}
          {isAdmin && (
            <div className="nav-light__mobile-section">
              <div className="nav-light__mobile-header">Admin</div>
              {adminItems.map((item) => (
                <button
                  key={item.path}
                  className="nav-light__mobile-link"
                  onClick={() => navigate(item.path)}
                >
                  <span>{item.label}</span>
                </button>
              ))}
            </div>
          )}

          {/* Sign Out */}
          <div className="nav-light__mobile-section">
            <button
              className="nav-light__mobile-link nav-light__mobile-link--danger"
              onClick={handleSignOut}
            >
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      )}

      {/* Unified Notification Drawer */}
      <NotificationDrawer
        open={notificationDrawerOpen}
        onClose={() => setNotificationDrawerOpen(false)}
        onCountChange={setNotificationCount}
      />
    </nav>
  );
}

export default NavigationLight;
